import { CreateButton, Show, useModal } from "@refinedev/antd";
import { Button, Space, Tag, Input, Row, Col } from "antd";
import { IResourceComponentsProps, useShow } from "@refinedev/core";
import {
  SafetyCertificateOutlined,
  RocketOutlined,
  BankOutlined,
  TeamOutlined,
  AppstoreOutlined,
  FileProtectOutlined,
  GlobalOutlined,
  BookOutlined,
  CompassOutlined,
} from "@ant-design/icons";

import dayjs from "dayjs";
import { ILicence } from "interfaces";
import prettyBytes from "pretty-bytes";
import React from "react";
import {
  ShowField,
  ShowSection,
  ShowFieldGrid,
  ShowModal,
  ShowModalItem,
} from "components/show";

export const LicenceShow: React.FC<IResourceComponentsProps> = () => {
  const { result, query: { isLoading } } = useShow<ILicence>();
  const record = result;

  const {
    modalProps: modalPropsVessel,
    show: showVessel,
    close: closeVessel,
  } = useModal();
  const {
    modalProps: modalPropsShipco,
    show: showShipco,
    close: closeShipco,
  } = useModal();
  const {
    modalProps: modalPropsShipcoGroup,
    show: showShipcoGroup,
    close: closeShipcoGroup,
  } = useModal();
  const {
    modalProps: modalPropsSubscription,
    show: showSubscription,
    close: closeSubscription,
  } = useModal();
  const {
    modalProps: modalPropsMetadataAvcs,
    show: showMetadataAvcs,
    close: closeMetadataAvcs,
  } = useModal();
  const {
    modalProps: modalPropsMetadataAdp,
    show: showMetadataAdp,
    close: closeMetadataAdp,
  } = useModal();
  const {
    modalProps: modalPropsMetadataEnp,
    show: showMetadataEnp,
    close: closeMetadataEnp,
  } = useModal();
  const {
    modalProps: modalPropsMetadataArcs,
    show: showMetadataArcs,
    close: closeMetadataArcs,
  } = useModal();

  const hasVessel = !!record?.vessel;
  const hasShipco = !!record?.shipco && record.shipco?.deletedAt !== null;
  const hasShipcoGroup = !!record?.shipcoGroup;
  const hasSubscription = !!record?.subscription;
  const hasAvcs = !!record?.subscription?.metadataAvcs;
  const hasAdp = !!record?.subscription?.metadataAdp;
  const hasEnp = !!record?.subscription?.metadataEnp;
  const hasArcs = !!record?.subscription?.metadataArcs;

  return (
    <Show
      breadcrumb={false}
      isLoading={isLoading}
      headerProps={{
        onBack: () => window.history.back(),
        subTitle: "active licence",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
      }}
      footerButtons={
        <Space wrap>
          {hasVessel ? (
            <Button type="default" onClick={showVessel}>
              Vessel Details
            </Button>
          ) : (
            <CreateButton resource="vessel">Setup Vessel</CreateButton>
          )}
          {hasShipco ? (
            <Button type="default" onClick={showShipco}>
              Associated Shipco
            </Button>
          ) : (
            <CreateButton resource="shipco">Setup Shipco</CreateButton>
          )}
          {hasShipcoGroup && (
            <Button type="default" onClick={showShipcoGroup}>
              Associated Shipco Group
            </Button>
          )}
          {hasSubscription ? (
            <Button type="default" onClick={showSubscription}>
              Associated Subscriptions
            </Button>
          ) : (
            <CreateButton resource="subscription">Setup Subscription</CreateButton>
          )}
          {hasAvcs ? (
            <Button type="default" onClick={showMetadataAvcs}>
              AVCS Service
            </Button>
          ) : (
            <CreateButton resource="metadata-avcs">Setup AVCS</CreateButton>
          )}
          {hasAdp ? (
            <Button type="default" onClick={showMetadataAdp}>
              ADP Service
            </Button>
          ) : (
            <CreateButton resource="metadata-adp">Setup ADP</CreateButton>
          )}
          {hasEnp ? (
            <Button type="default" onClick={showMetadataEnp}>
              e-NP Service
            </Button>
          ) : (
            <CreateButton resource="metadata-enp">Setup e-NP</CreateButton>
          )}
          {hasArcs ? (
            <Button type="default" onClick={showMetadataArcs}>
              ARCS Service
            </Button>
          ) : (
            <CreateButton resource="metadata-arcs">Setup ARCS</CreateButton>
          )}
        </Space>
      }
    >
      <Row gutter={[24, 24]}>
        {/* Left Column - Primary Info */}
        <Col xs={24} lg={12}>
          <ShowSection
            title="Licence Overview"
            subtitle="Primary licence identification"
            icon={<SafetyCertificateOutlined />}
            variant="elevated"
          >
            <ShowFieldGrid columns={1}>
              <ShowField label="UKHO Licence" value={record?.ukhoLicenceId} type="code" copyable />
              <ShowField label="Invoice Delivery" value={record?.invoiceDelivery} type="tag" tagColor="blue" />
              <ShowField label="Purchase Order Required" value={record?.purchaseOrderReqd} type="status" />
            </ShowFieldGrid>
          </ShowSection>
        </Col>

        {/* Right Column - Vessel Info */}
        <Col xs={24} lg={12}>
          <ShowSection
            title="Vessel Information"
            subtitle="Associated vessel details"
            icon={<RocketOutlined />}
            variant="elevated"
          >
            {hasVessel ? (
              <ShowFieldGrid columns={1}>
                <ShowField label="Vessel Name" value={record?.vessel?.name} />
                <ShowField label="IMO Number" value={record?.vessel?.imoNumber} type="code" copyable />
                <ShowField label="Vessel Type" value={record?.vessel?.type} type="tag" tagColor="purple" />
                <ShowField label="Status" value={record?.vessel?.holdStatus} type="status" />
              </ShowFieldGrid>
            ) : (
              <div style={{ color: "#999", fontStyle: "italic" }}>No vessel associated</div>
            )}
          </ShowSection>
        </Col>
      </Row>

      {/* Vessel Modal */}
      <ShowModal
        title="Vessel Details"
        subtitle={record?.vessel?.name}
        icon={<RocketOutlined />}
        open={modalPropsVessel.open ?? false}
        onClose={closeVessel}
        empty={!hasVessel}
        emptyText="No vessel associated with this licence"
        width={700}
      >
        <Space direction="vertical" size={20} style={{ width: "100%" }}>
          <ShowFieldGrid columns={2}>
            <ShowField label="Vessel Name" value={record?.vessel?.name} />
            <ShowField label="Vessel Type" value={record?.vessel?.type} type="tag" tagColor="blue" />
            <ShowField label="UKHO Licence" value={record?.ukhoLicenceId} type="code" copyable />
            <ShowField label="IMO Number" value={record?.vessel?.imoNumber} type="code" copyable />
            <ShowField label="Communication Email" value={record?.vessel?.email} type="email" />
            <ShowField label="Call Sign" value={record?.vessel?.callSign} type="code" />
            <ShowField label="Max Email Size" value={record?.vessel?.maxEmailSizeKb ? prettyBytes(Number(record.vessel.maxEmailSizeKb)) : null} />
            <ShowField label="Max Transmission Size" value={record?.vessel?.maxTransmissionSizeMb ? prettyBytes(Number(record.vessel.maxTransmissionSizeMb) * 1024 * 1024) : null} />
            <ShowField label="Status" value={record?.vessel?.holdStatus} type="status" />
          </ShowFieldGrid>
          {record?.vessel?.notes && (
            <div>
              <ShowField label="Notes" value="" />
              <Input.TextArea rows={4} readOnly value={record?.vessel?.notes} style={{ marginTop: 8 }} />
            </div>
          )}
        </Space>
      </ShowModal>

      {/* Shipco Modal */}
      <ShowModal
        title="Shipping Company"
        subtitle={record?.shipco?.registrationName}
        icon={<BankOutlined />}
        open={modalPropsShipco.open ?? false}
        onClose={closeShipco}
        empty={!hasShipco}
        emptyText="No shipping company associated with this licence"
      >
        <ShowFieldGrid columns={2}>
          <ShowField label="Registration Name" value={record?.shipco?.registrationName} />
          <ShowField label="Registration Number" value={record?.shipco?.registrationNumber} type="code" copyable />
          <ShowField label="GST Number" value={record?.shipco?.gstNumber} type="code" copyable />
          <ShowField label="Phone" value={record?.shipco?.phone} type="phone" />
          <ShowField label="Communication Email" value={record?.shipco?.email} type="email" />
        </ShowFieldGrid>
      </ShowModal>

      {/* Shipco Group Modal */}
      <ShowModal
        title="Shipco Group"
        subtitle={record?.shipcoGroup?.registrationName}
        icon={<TeamOutlined />}
        open={modalPropsShipcoGroup.open ?? false}
        onClose={closeShipcoGroup}
        empty={!hasShipcoGroup}
        emptyText="No shipco group associated with this licence"
      >
        <ShowFieldGrid columns={2}>
          <ShowField label="Registration Name" value={record?.shipcoGroup?.registrationName} />
          <ShowField label="Registration Number" value={record?.shipcoGroup?.registrationNumber} type="code" copyable />
          <ShowField label="GST Number" value={record?.shipcoGroup?.gstNumber} type="code" copyable />
          <ShowField label="Phone" value={record?.shipcoGroup?.phone} type="phone" />
          <ShowField label="Communication Email" value={record?.shipcoGroup?.email} type="email" />
        </ShowFieldGrid>
      </ShowModal>

      {/* Subscription Modal */}
      <ShowModal
        title="Subscription Details"
        subtitle={record?.subscription?.subscriptionName}
        icon={<AppstoreOutlined />}
        open={modalPropsSubscription.open ?? false}
        onClose={closeSubscription}
        empty={!hasSubscription}
        emptyText="No subscription associated with this licence"
        width={700}
      >
        <ShowFieldGrid columns={2}>
          <ShowField label="Subscription Name" value={record?.subscription?.subscriptionName} />
          <ShowField label="Service Type" value={record?.subscription?.serviceType} type="tag" tagColor="purple" />
          <ShowField
            label="Enrollment Date"
            value={record?.subscription?.enrollmentDate ? dayjs(record?.subscription?.enrollmentDate).format("DD-MMM-YYYY") : undefined}
          />
          <ShowField label="Licence Type" value={record?.subscription?.licenceType} type="status" />
          <ShowField label="Activation Status" value={record?.subscription?.activationStatus} type="status" />
          <ShowField label="Hold Status" value={record?.subscription?.holdStatus} type="status" />
          <ShowField label="Order Value Threshold" value={record?.subscription?.orderValueThreshold} type="currency" />
          <ShowField label="Automated Ordering" value={record?.subscription?.automatedOrdering} type="status" />
          <ShowField label="Automated Permit Issuance" value={record?.subscription?.automatedPermitIssuance} type="status" />
        </ShowFieldGrid>
      </ShowModal>

      {/* AVCS Metadata Modal */}
      <ShowModal
        title="AVCS Service Details"
        subtitle={record?.vessel?.name}
        icon={<GlobalOutlined />}
        open={modalPropsMetadataAvcs.open ?? false}
        onClose={closeMetadataAvcs}
        empty={!hasAvcs}
        emptyText="No AVCS service configured"
        width={750}
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <ShowFieldGrid columns={2}>
            <ShowField label="UKHO Licence" value={record?.ukhoLicenceId} type="code" copyable />
            <ShowField label="IMO Number" value={record?.vessel?.imoNumber} type="code" />
            <ShowField label="Vessel Name" value={record?.vessel?.name} />
            <ShowField label="Licence Type" value={record?.subscription?.metadataAvcs?.licenceType} type="status" />
            <ShowField label="Expiry Date" value={record?.subscription?.metadataAvcs?.expiryDate} type="date" />
          </ShowFieldGrid>

          <ShowSection title="Permit Configuration" variant="subtle" padding="compact">
            <ShowFieldGrid columns={2}>
              <ShowField label="Master Permit" value={record?.subscription?.metadataAvcs?.masterPermit} type="code" copyable />
              <ShowField label="Master Permit Version" value={record?.subscription?.metadataAvcs?.masterPermitVersion} />
              <ShowField label="Backup Permit" value={record?.subscription?.metadataAvcs?.backupPermit} type="code" copyable />
              <ShowField label="Backup Permit Version" value={record?.subscription?.metadataAvcs?.backupPermitVersion} />
            </ShowFieldGrid>
          </ShowSection>

          <ShowFieldGrid columns={2}>
            <ShowField label="Certificate URL" value={record?.subscription?.metadataAvcs?.certificateUrl} type="url" />
            <ShowField label="Schedule-A URL" value={record?.subscription?.metadataAvcs?.scheduleAUrl} type="url" />
          </ShowFieldGrid>
        </Space>
      </ShowModal>

      {/* ADP Metadata Modal */}
      <ShowModal
        title="ADP Service Details"
        subtitle={record?.vessel?.name}
        icon={<BookOutlined />}
        open={modalPropsMetadataAdp.open ?? false}
        onClose={closeMetadataAdp}
        empty={!hasAdp}
        emptyText="No ADP service configured"
        width={750}
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <ShowFieldGrid columns={2}>
            <ShowField label="UKHO Licence" value={record?.ukhoLicenceId} type="code" copyable />
            <ShowField label="IMO Number" value={record?.vessel?.imoNumber} type="code" />
            <ShowField label="Vessel Name" value={record?.vessel?.name} />
            <ShowField label="Licence Type" value={record?.subscription?.metadataAdp?.licenceType} type="status" />
            <ShowField label="TID" value={record?.subscription?.metadataAdp?.tid} type="code" copyable />
            <ShowField label="Startup Key" value={record?.subscription?.metadataAdp?.startupKey} type="code" copyable />
            <ShowField label="Expiry Date" value={record?.subscription?.metadataAdp?.expiryDate} type="date" />
          </ShowFieldGrid>

          <ShowSection title="Master Machine" variant="subtle" padding="compact">
            <ShowFieldGrid columns={2}>
              <ShowField label="Machine Name" value={record?.subscription?.metadataAdp?.masterMachineName} />
              <ShowField label="Machine ID" value={record?.subscription?.metadataAdp?.masterMachineId} type="code" copyable />
              <ShowField label="Machine Key" value={record?.subscription?.metadataAdp?.masterMachineKey} type="code" copyable />
              <ShowField label="Machine Version" value={record?.subscription?.metadataAdp?.masterMachineVersion} />
            </ShowFieldGrid>
          </ShowSection>

          <ShowSection title="Backup Machine" variant="subtle" padding="compact">
            <ShowFieldGrid columns={2}>
              <ShowField label="Machine Name" value={record?.subscription?.metadataAdp?.backupMachineName} />
              <ShowField label="Machine ID" value={record?.subscription?.metadataAdp?.backupMachineId} type="code" copyable />
              <ShowField label="Machine Key" value={record?.subscription?.metadataAdp?.backupMachineKey} type="code" copyable />
              <ShowField label="Machine Version" value={record?.subscription?.metadataAdp?.backupMachineVersion} />
            </ShowFieldGrid>
          </ShowSection>
        </Space>
      </ShowModal>

      {/* ENP Metadata Modal */}
      <ShowModal
        title="e-NP Service Details"
        subtitle={record?.vessel?.name}
        icon={<FileProtectOutlined />}
        open={modalPropsMetadataEnp.open ?? false}
        onClose={closeMetadataEnp}
        empty={!hasEnp}
        emptyText="No e-NP service configured"
        width={700}
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <ShowFieldGrid columns={2}>
            <ShowField label="UKHO Licence" value={record?.ukhoLicenceId} type="code" copyable />
            <ShowField label="IMO Number" value={record?.vessel?.imoNumber} type="code" />
            <ShowField label="Vessel Name" value={record?.vessel?.name} />
            <ShowField label="Licence Type" value={record?.subscription?.metadataEnp?.licenceType} type="status" />
            <ShowField label="Expiry Date" value={record?.subscription?.metadataEnp?.expiryDate} type="date" />
          </ShowFieldGrid>

          <ShowSection title="Master Configuration" variant="subtle" padding="compact">
            <ShowFieldGrid columns={2}>
              <ShowField label="User Permit" value={record?.subscription?.metadataEnp?.masterUserPermit} type="code" copyable />
              <ShowField label="Machine ID" value={record?.subscription?.metadataEnp?.masterMachineId} type="code" copyable />
              <ShowField label="e-Reader Version" value={record?.subscription?.metadataEnp?.masterEreaderVersion} />
            </ShowFieldGrid>
          </ShowSection>

          <ShowSection title="Backup Configuration" variant="subtle" padding="compact">
            <ShowFieldGrid columns={2}>
              <ShowField label="User Permit" value={record?.subscription?.metadataEnp?.backupUserPermit} type="code" copyable />
              <ShowField label="Machine ID" value={record?.subscription?.metadataEnp?.backupMachineId} type="code" copyable />
              <ShowField label="e-Reader Version" value={record?.subscription?.metadataEnp?.backupEreaderVersion} />
            </ShowFieldGrid>
          </ShowSection>
        </Space>
      </ShowModal>

      {/* ARCS Metadata Modal */}
      <ShowModal
        title="ARCS Service Details"
        subtitle={record?.vessel?.name}
        icon={<CompassOutlined />}
        open={modalPropsMetadataArcs.open ?? false}
        onClose={closeMetadataArcs}
        empty={!hasArcs}
        emptyText="No ARCS service configured"
        width={700}
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <ShowFieldGrid columns={2}>
            <ShowField label="UKHO Licence" value={record?.ukhoLicenceId} type="code" copyable />
            <ShowField label="IMO Number" value={record?.vessel?.imoNumber} type="code" />
            <ShowField label="Vessel Name" value={record?.vessel?.name} />
            <ShowField label="Licence Type" value={record?.subscription?.metadataArcs?.licenceType} type="status" />
            <ShowField label="Expiry Date" value={record?.subscription?.metadataArcs?.expiryDate} type="date" />
          </ShowFieldGrid>

          <ShowSection title="Permit Configuration" variant="subtle" padding="compact">
            <ShowFieldGrid columns={2}>
              <ShowField label="Master Permit" value={record?.subscription?.metadataArcs?.masterPermit} type="code" copyable />
              <ShowField label="Master Permit Pin" value={record?.subscription?.metadataArcs?.masterPermitPin} type="code" copyable />
              <ShowField label="Backup Permit" value={record?.subscription?.metadataArcs?.backupPermit} type="code" copyable />
              <ShowField label="Backup Permit Pin" value={record?.subscription?.metadataArcs?.backupPermitPin} type="code" copyable />
            </ShowFieldGrid>
          </ShowSection>
        </Space>
      </ShowModal>
    </Show>
  );
};
